// SINGLE SOURCE OF TRUTH for how plan capabilities are presented on BOTH the
// public pricing page and the authenticated subscription page. The DATA comes
// from GET /api/plans (plan.capabilities mirrors the server PLAN_FEATURES matrix);
// this file only maps each capability to a category, a human label and a tooltip.
// Never advertise a capability here that the API doesn't report as granted.

export const PLAN_RANK = { free: 0, basic: 1, personal: 2, family: 3, premium: 4, business: 5 };

export const CATEGORY_ORDER = ["STORE", "SCAN", "SIGN", "VERIFY", "AUTOMATE", "PROTECT"];

// capability key -> { cat, label, tip }
export const CAP_META = {
  password_links: { cat: "STORE", label: "Password-protected links", tip: "Add a password to any shared link so only the right people can open it." },
  advanced_sharing: { cat: "STORE", label: "Advanced sharing controls", tip: "Protected & maximum-security links: one-time codes, view limits and email restrictions." },
  duruscan: { cat: "SCAN", label: "DuruScan + OCR", tip: "Intelligent document scanning that also extracts text with OCR." },
  durusign: { cat: "SIGN", label: "DuruSign e-signatures", tip: "Send documents to others for a secure electronic signature." },
  duruverify: { cat: "VERIFY", label: "DuruVerify", tip: "Identity and document verification for people you transact with (early access)." },
  id_check: { cat: "VERIFY", label: "ID Check", tip: "Run a government-ID verification check (early access)." },
  durutwin: { cat: "AUTOMATE", label: "DuruTwin", tip: "Creates an intelligent digital representation of important documents that tracks metadata, relationships, status and next actions — while preserving the original." },
  expiration_reminders: { cat: "PROTECT", label: "Expiration Intelligence", tip: "Monitors document expiry dates and reminds you before they lapse." },
  dururooms: { cat: "PROTECT", label: "DuruRooms secure spaces", tip: "Shared, access-controlled rooms for teams and deals." },
  audit_logs: { cat: "PROTECT", label: "Audit logs", tip: "A tamper-evident history of who did what, for compliance." },
  compliance: { cat: "PROTECT", label: "Compliance controls", tip: "Organization policy and compliance tooling." },
};

// Build ordered, categorized capability groups for a plan from its API payload.
// Only GRANTED capabilities appear (coming-soon items are false in the matrix and
// are naturally excluded). STORE always leads with base secure storage.
export function planGroups(plan) {
  const caps = plan.capabilities || {};
  const early = new Set(plan.early_access || []);
  const groups = {};
  for (const [cap, on] of Object.entries(caps)) {
    if (!on) continue;
    const meta = CAP_META[cap];
    if (!meta) continue;
    (groups[meta.cat] = groups[meta.cat] || []).push({ cap, label: meta.label, tip: meta.tip, early: early.has(cap) });
  }
  (groups.STORE = groups.STORE || []).unshift({ cap: "_storage", label: "Secure cloud storage", tip: null });
  return CATEGORY_ORDER.filter((c) => groups[c] && groups[c].length).map((c) => ({ cat: c, items: groups[c] }));
}

export function scanLabel(n) {
  return n == null ? "Unlimited scans" : `${Number(n).toLocaleString()} scans/mo`;
}
export function ocrLabel(n) {
  return n == null ? "Unlimited OCR" : `${Number(n).toLocaleString()} OCR/mo`;
}

// Real annual savings for a plan, from its CONFIGURED monthly/annual amounts
// (GET /api/plans). Returns an integer percent, or null when it can't be computed
// (missing/zero price or no genuine saving). Never fabricate a number.
export function annualSavingsPct(plan) {
  const m = plan?.amount_monthly, y = plan?.amount_yearly;
  if (!m || !y || m <= 0 || y <= 0) return null;
  const pct = Math.round((1 - y / (m * 12)) * 100);
  return pct > 0 ? pct : null;
}

// Largest real annual saving across the given plans (for the toggle's "Save up to X%").
export function maxAnnualSavingsPct(plans) {
  const vals = (plans || []).map(annualSavingsPct).filter((v) => v != null);
  return vals.length ? Math.max(...vals) : null;
}

// A plan can be shown/bought yearly only when annual billing is live AND it has a
// real configured annual price. Keeps display == checkout (no silent fallback).
export function planHasYearly(plan) {
  return !!(plan?.annual_available && plan?.amount_yearly > 0);
}
